import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Space,
  Form,
  Tooltip,
  Row,
  Col,
  Table,
  Upload,
  Alert,
  Tag,
  notification,
} from "antd";

import {
  UploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeFilled,
  DownloadOutlined,
  CheckOutlined,
  SendOutlined,
  InboxOutlined
} from "@ant-design/icons";

import "../../styles/seims.css";
import {
  Messages,
  Notify,
  openNotification,
  progressIndicatorEnum,
  docsEnum,
  FileCard,
  pdfjsVersion,
  util,
} from "@seims/shared";
import lookupApi from "../../services/candidateLookupService";
import api from "../../services/nysService";

import { Viewer, Worker } from "@react-pdf-viewer/core";
// Plugins
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { recordError } from "../../utils/logService";

const formItemLayout = {
  labelCol: {
    span: 12,
  },
  wrapperCol: {
    span: 18,
  },
};

const SupportingDocument = ({
  onProgressUpdate,
  docHasUpdated,
  setDocHasUpdated,
  data,
  readOnly,
}) => {
  var msg;

  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const [form] = Form.useForm();
  const formName = "SupportingDocsForm";
  const { confirm } = Modal;
  const { Dragger } = Upload;

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState({});
  const [docTypes, setDocTypes] = useState([]);
  const [progress, setProgress] = useState(0);
  const [replaceDoc, setReplaceDoc] = useState(false);
  const [newDoc, setNewDoc] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [toggleFiles, setToggleFiles] = useState(false);
  const NUMBER_OF_DOCUMENTS = 5;

  const columns = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "1",
    },
    {
      title: "Document Description",
      dataIndex: "typeDescription",
      key: "2",
    },
    {
      title: "Upload Date",
      dataIndex: "uploadDate",
      key: "3",
    },
    {
      title: "Replace/Remove",
      dataIndex: "delete",
      key: "4",
      render: (_, record) => {
        if (record.id > 0) {
          return (
            <>
              <Tooltip title={"Click to Replace the file"}>
                <Upload
                  key={1}
                  defaultFileList={[]}
                  {...uploadInfo}
                  maxCount={1}
                  multiple={false}
                >
                  <Button
                    onClick={() => handleFileSelection(record)}
                    icon={<UploadOutlined />}
                    // disabled={readOnly}
                  ></Button>
                </Upload>
              </Tooltip>
            </>
          );
        } else {
          return (
            <Tooltip title={"Click to Remove the file"}>
              <Button
                type="danger"
                icon={<DeleteOutlined />}
                onClick={() => handleFileRemove(record)}
                disabled={readOnly}
              />
            </Tooltip>
          );
        }
      },
    },
    {
      title: "Preview/Download",
      dataIndex: "preview",
      key: "5",
      render: (_, record) => {
        if (record.id > 0) {
          return (
            <>
              <Space>
                <Button
                  type="primary"
                  icon={<EyeFilled />}
                  onClick={() => PreviewDocument(record)}
                  // disabled={readOnly}
                />
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleFileDownload(record)}
                  // disabled={readOnly}
                />
              </Space>
            </>
          );
        } else {
          return (
            <>
              <Tag color="volcano" key={record.fileName}>
                {"NOT SAVED YET".toUpperCase()}
              </Tag>
            </>
          );
        }
      },
    },
  ];

  const handleCancel = () => setPreviewOpen(false);

  useEffect(() => {
    getDocTypes();
  }, []);

  useEffect(() => {
    getDocuments();
  }, [data, docHasUpdated]);

  useEffect(() => {
    if (replaceDoc) {
      updateDocumentList(newDoc, true);
    }
  }, [replaceDoc]);

  const getDocTypes = async () => {
    try {
      const response = await lookupApi.getDocTypes();

      if (response) {
        const { data } = response;
        // const _data = data.filter(c=>c.value !== 'Motivation Letter').map((c) => ({
        const _data = data.map((c) => ({
          key: c.key,
          value: c.value,
          id: c.value.replaceAll(" ", "_").toString().trim(),
        }));

        setDocTypes(_data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getDocuments = () => {
    const _progress = (data.length / NUMBER_OF_DOCUMENTS) * 100;
    if (data.length > 0 && data.length < NUMBER_OF_DOCUMENTS) {
      onProgressUpdate(progressIndicatorEnum.IN_PROGRESS, _progress);
    } else if (data.length === NUMBER_OF_DOCUMENTS) {
      onProgressUpdate(progressIndicatorEnum.COMPLETED, _progress);
    } else {
      onProgressUpdate(progressIndicatorEnum.NOT_STARTED, _progress);
    }
    fileBuilder();
    setProgress(_progress);
  };

  const fileBuilder = () => {
    let fileData = [{}];

    fileData = data.map((item) => ({
      fileName: item.filename,
      size: item.fileSize,
      type: item.contentType,
      documentType: item.documentType.key,
      typeDescription: item.documentType.value,
      uploadDate: item.lastModified,
      file: item.file === undefined ? {} : item.file,
      id: item.id,
      userId: item.userId,
      hasFile: false,
    }));

    setUploadedFiles(fileData);
  };

  const handleUpload = (doc) => {
    const allowedFileTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/jpg",
    ];

    const fSize = doc.size / 1024 / 1024;

    if (!allowedFileTypes.includes(doc.type)) {
      openNotification(
        "File Validation",
        `${doc.name} is not a valid file type`,
        Notify.ERROR
      );
      return Upload.LIST_IGNORE;
    }

    notification.destroy("seims");

    if (fSize > NUMBER_OF_DOCUMENTS) {
      openNotification(
        "File Size",
        "File must be smaller than 5MB!",
        Notify.WARNING
      );

      return Upload.LIST_IGNORE;
    }

    setNewDoc(doc);

    // _selectedDocType = selectedDocType.key == undefined ? selectedDocType : selectedDocType.key;

    const selectedDoc = uploadedFiles.filter(
      (c) => c.documentType === selectedDocType.key
    );

    if (selectedDoc.length > 0) {
      const fileName = selectedDoc[0].fileName;
      confirmDocReplacement(fileName);

      //? DO NOT UPLOAD DOC IF USER DOESN'T WANT TO REPLACE
      if (!replaceDoc) return Upload.LIST_IGNORE;
      updateDocumentList(doc, true);
      return false;
    }

    //? ONLY ADD UNIQUE ITEMS BASED ON THE DOCUMENT TYPE
    setReplaceDoc(false);
    updateDocumentList(doc, false);
    return false;
  };

  const handleFileDownload = async (record) => {
    try {
      if (!isFileViewable(record, "Download")) {
        return;
      }

      const response = await api.getFile(record.id, record.userId);
      if (response && response.status == 200) {
        const { data } = response;
        const new_blob = new Blob([data]);

        const url = URL.createObjectURL(new_blob);
        const downloadLink = document.createElement("a");

        downloadLink.href = url;
        downloadLink.download = record.fileName;
        downloadLink.click();
        URL.revokeObjectURL(url);
        return;
      }

      msg = util.parseMessage(Messages.GET_FAILURE, "File");
      openNotification("File Download", msg, Notify.ERROR);
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, "File");
      openNotification("File Download", msg, Notify.ERROR);
      recordError(error);
    }
  };

  const updateDocumentList = (doc, replace) => {
    let fileData = [{}];
    var today = new Date();
    let _uploadedFiles = [];
    let _progress;

    fileData.fileName = doc.name;
    fileData.size = doc.size;
    fileData.type = doc.type;
    fileData.documentType = selectedDocType.key;
    fileData.typeDescription = selectedDocType.value;
    fileData.uploadDate = today.toLocaleDateString("en-US");
    fileData.file = doc;
    fileData.hasFile = true;

    if (replace) {
      let existingDoc = uploadedFiles.find(
        (c) => c.documentType === selectedDocType.key
      );

      fileData.id = existingDoc.id;

      const newFiles = uploadedFiles.filter(
        (c) => c.documentType !== selectedDocType.key
      );
      _uploadedFiles = [...newFiles, fileData];
    } else {
      //? THIS IS FOR THE DEFAULT DCOCUMENT THAT HAS NOT BEEN SAVED
      fileData.id = 0;
      _uploadedFiles = [...uploadedFiles, fileData];
    }

    _progress = (_uploadedFiles.length / NUMBER_OF_DOCUMENTS) * 100;
    setUploadedFiles(_uploadedFiles);
    setProgress(_progress);
  };

  const uploadInfo = {
    accept: ".png, .pdf, .jpg, jpeg",
    multiple: false,
    showUploadList: { showRemoveIcon: false },
    beforeUpload: handleUpload,
    onRemove: () => {
      setNewDoc(null);
    },
  };

  const handleFileRemove = (file) => {
    const { typeDescription } = file;

    if (file.id > 0) {
      const msg = util.parseMessage(Messages.FILE_ALREADY_SAVED, "NYS");
      openNotification("File Removal", msg, Notify.ERROR);
      return;
    }

    if (typeDescription === docsEnum.CURRICULUM_VITAE) {
      form.setFieldsValue({ "Curriculum Vitae (CV)": [] });
    } else if (typeDescription === docsEnum.QUALIFICATION) {
      form.setFieldsValue({ "Copy of Highest Qualification": [] });
    } else if (typeDescription === docsEnum.CERTIFIED_ID) {
      form.setFieldsValue({ "Certified ID Copy": [] });
    } else if (typeDescription === docsEnum.GRADE_12_CERT) {
      form.setFieldsValue({ "Grade 12 Certified Copy": [] });
    } else {
      form.setFieldsValue({ "Motivation Letter": [] });
    }

    const _uploadedFiles = [...uploadedFiles];
    const _progress = (_uploadedFiles.length / NUMBER_OF_DOCUMENTS) * 100;

    setProgress(_progress);

    setUploadedFiles((current) =>
      current.filter((element) => {
        return element.documentType !== file.documentType;
      })
    );
  };

  const handleFileSelection = (selectedDocType) => {
    if (selectedDocType.documentType) {
      const { documentType } = selectedDocType;
      const selected = docTypes.find((c) => c.key === documentType);

      setSelectedDocType(selected);
    } else setSelectedDocType(selectedDocType);
  };

  const handleFileDrop = (item) => {};
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const onFinishFailed = (values) => {};

  const onSubmit = async () => {
    try {
      const newFileUploades = uploadedFiles.filter(
        (c) => c.hasFile === true
      ).length;
      if (newFileUploades === 0) {
        msg = util.parseMessage(Messages.FILE_UPLOAD_NO_FILE, "NYS");
        openNotification("File upload", msg, Notify.WARNING);
        return;
      }

      const request = await api.addSuppDocuments(uploadedFiles);

      if (request.status == 200 || request.status == 201) {
        if (progress == 100)
          onProgressUpdate(progressIndicatorEnum.COMPLETED, progress);
        else onProgressUpdate(progressIndicatorEnum.IN_PROGRESS, progress);

        msg = util.parseMessage(Messages.ADD_SUCCESS_DOC, "Documents");
        openNotification("File upload", msg, Notify.SUCCESS);

        form.resetFields();
        setUploadedFiles([]);
        setDocHasUpdated(!docHasUpdated);
        return;
      }

      msg = util.parseMessage(Messages.ADD_FAILURE_DOC, "Documents");
      openNotification("File upload", msg, Notify.ERROR);
    } catch (error) {
      msg = util.parseMessage(Messages.ADD_FAILURE_DOC, "Documents");
      openNotification("File upload", msg, Notify.ERROR);
      recordError(error);
    }
  };

  const confirmDocReplacement = (docName) => {
    confirm({
      title: `Are you sure you want to replace document (${docName})?`,
      icon: <ExclamationCircleOutlined />,
      content: "Confirm replacement",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",

      onOk() {
        setReplaceDoc(true);
      },

      onCancel() {
        setReplaceDoc(false);
      },
    });
  };

  const PreviewDocument = async (record) => {
    try {
      if (!isFileViewable(record, "Preview")) {
        return;
      }

      const response = await api.getFile(record.id, record.userId);

      if (response && response.status == 200) {
        const { data } = response;
        const new_blob = new Blob([data]);

        const url = URL.createObjectURL(new_blob);

        setPreviewImage(url || record.preview);
        setPreviewOpen(true);
        setPreviewTitle(record.fileName);
        setToggleFiles(record.type != "application/pdf");
        // URL.revokeObjectURL(url);
        return;
      }

      msg = util.parseMessage(Messages.GET_FAILURE, "File");
      openNotification("File Preview", msg, Notify.ERROR);
    } catch (error) {
      msg = util.parseMessage(Messages.GET_FAILURE, "File");
      openNotification("File Preview", msg, Notify.ERROR);
      recordError(error);
    }
  };

  const isFileViewable = (record, label) => {
    const noIdentifiers =
      record.userId === undefined ||
      record.userId === null ||
      record.id === undefined ||
      record.id === null ||
      record.id < 1;

    if (noIdentifiers) {
      const message = "Document " + label;
      const desciption =
        "Unable to " +
        label.toLowerCase() +
        " a new or replacement file before saving." +
        " Please save or reload the page to discard changes";
      openNotification(message, desciption, Notify.WARNING);
    }
    return !noIdentifiers;
  };

  const title = "Supporting Documents";
  // const subTitle = "Upload your documents (max 5MB for each file)";

  return (
    <>
      <h1 className="center-align">{title}</h1>
      {/* <p className="sub-title center-align">{subTitle}</p> */}
      <br />
      <Alert
        message="Things to note: "
        description={
          <>
            1. At least 1 or more documents should be uploaded at a time before
            saving. In order to submit, you must upload all{" "}
            {NUMBER_OF_DOCUMENTS} documents. <br />
            2. Accepted file types are as follows: .png, .pdf, .jpg, jpeg.{" "}
            <br />
            3. You can upload Copy of Highest Qualification or a{" "}
            <b>letter of academic standing</b> <br />
            4. Please upload certified copies of your ID, Highest Qualification
            and Grade 12 Certificate.<br />
            5. Upload your documents (max 5MB for each file)
          </>
        }
        type="info"
        showIcon
      />
      <br />
      {/* {uploadedFiles.length === 0 && (
        <>
        </>
      )} */}
      <Form
        {...formItemLayout}
        name={formName}
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        // disabled={readOnly}
        key="1form"
      >
        <Row>
          {docTypes.map((item, index) => (
            <Col xs={24} md={12}>
              <Form.Item
                name={item.value}
                label={item.value}
                valuePropName="fileList"
                getValueFromEvent={normFile}
              >
                <Upload
                  key={index}
                  defaultFileList={[]}
                  {...uploadInfo}
                  maxCount={1}
                  multiple={false}
                >
                  <Button
                    key={index}
                    icon={<UploadOutlined />}
                    onClick={() => handleFileSelection(item)}
                  >
                    Browse
                  </Button>
                  {uploadedFiles.find(
                    (c) => c.typeDescription === item.value
                  ) && (
                    <Space style={{ paddingLeft: 6 }}>
                      <CheckOutlined style={{ color: "green" }} />
                    </Space>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Form>
      <Row key="2row">
        <div key="grid__fileupload" className="grid__fileupload">
          <Table
            dataSource={[...uploadedFiles]}
            columns={columns}
            // columns={
            //   readOnly
            //     ? columns.filter((c) => c.dataIndex !== "delete")
            //     : columns
            // }

            rowKey={(record) => record.documentType}
            bordered
            pagination={{ pageSize: 10 }}
            size="middle"
          />
        </div>
        <div key="card__fileupload" className="card__fileupload">
          {uploadedFiles.map((file, index) => (
            <FileCard
              key={index}
              onPreview={() => PreviewDocument(file)}
              onFileRemove={() => handleFileRemove(file)}
              onDownloadFile={() => handleFileDownload(file)}
              onFileSelection={() => handleFileSelection(file)}
              onUpload={() => handleUpload(file)}
              uploadInfo={uploadInfo}
              file={file}
              disabled={readOnly}
            />
          ))}
        </div>
      </Row>
      <Row key="row_submit" justify="end" style={{paddingBottom:"15px",paddingRight:"15px"}}>
        <Col xs={24}>
          <Button
            className="right-align-button"
            key="2b"
            htmlType="submit"
            form={formName}
            type="primary"
            shape="round"
            // disabled={readOnly}
            style={{ marginLeft: "40px", marginTop: "20px" }}
          >
            Save Supporting Documents
          </Button>
        </Col>
      </Row>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
        width="50%"
        key="mod1"
        // bodyStyle={{ height: 700 }}
      >
        {toggleFiles ? (
          <img
            alt="example"
            style={{
              width: "100%",
            }}
            src={previewImage}
          />
        ) : (
          <Worker
            workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion()}/build/pdf.worker.min.js`}
          >
            <Viewer
              fileUrl={previewImage}
              plugins={[defaultLayoutPluginInstance]}
              renderError={console.log}
            />
          </Worker>
        )}
      </Modal>
    </>
  );
};

export default SupportingDocument;
